// src/hooks/useGuideFeed.ts
//
// Three concerns split across three exports:
//
//   useGuideFeed — listens to /users/{uid}/guide_feed/, returns the
//     ordered card list (newest first), filters dismissed/expired,
//     and exposes like/dismiss/delete actions.
//
//   useGuideFeedBackground — mounts ONCE at the app root. Drives the
//     generation schedule:
//       - First-mount prime when feed is empty/stale (within 30s of mount)
//       - Periodic top-up every 5 min while app visible
//       - Significant location change (>2km) → fire generation
//       - Tab-becomes-visible after >2 min away → top-up
//
//   useTriggerGuideFeed — imperative top-up call. Used by the page
//     when scroll approaches the bottom.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { useLocation } from './useLocation';
import { useAuthStore } from '../stores/authStore';
import { getLanguageHint } from '../utils/getLanguageHint';
import { writeFeedItem } from '../feed/writeFeedItem';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

export type GuideCardKind =
  | 'place'
  | 'event'
  | 'list'
  | 'prompt'
  | 'suggestion'
  | 'tip'
  | 'story';

export interface GuideListEntry {
  title: string;
  blurb: string;
  chat_prompt?: string;
}

export interface GuideFeedCard {
  id: string;
  kind: GuideCardKind;
  title: string;
  body: string;
  meta?: string;
  chat_prompt?: string;
  place_name?: string;
  place_id?: string;
  photo_url?: string;
  location?: { lat: number; lng: number };
  tags: string[];
  list_items?: GuideListEntry[];
  for_city?: string;
  for_country?: string;
  created_at: Date | null;
  expires_at: Date | null;
  dismissed: boolean;
  liked: boolean;
}

// ────────────────────────────────────────────────────────────
// Listener hook — used by GuidePage
// ────────────────────────────────────────────────────────────

export const useGuideFeed = () => {
  const [cards, setCards] = useState<GuideFeedCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let unsubSnap: (() => void) | null = null;
    let currentUid: string | null = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user?.uid === currentUid) return;
      if (unsubSnap) {
        unsubSnap();
        unsubSnap = null;
      }
      currentUid = user?.uid || null;
      if (!user) {
        setCards([]);
        setIsLoading(false);
        return;
      }
      const ref = collection(db, 'users', user.uid, 'guide_feed');
      const q = query(ref, orderBy('created_at', 'desc'));
      unsubSnap = onSnapshot(
        q,
        (snap) => {
          const out: GuideFeedCard[] = [];
          snap.forEach((d) => {
            const data = d.data() as any;
            // Skip dismissed (we already wrote that flag client-side)
            if (data?.dismissed) return;
            out.push({
              id: d.id,
              kind: data.kind || 'tip',
              title: data.title || '',
              body: data.body || '',
              meta: data.meta,
              chat_prompt: data.chat_prompt,
              place_name: data.place_name,
              place_id: data.place_id,
              photo_url: data.photo_url,
              location: data.location,
              tags: Array.isArray(data.tags) ? data.tags : [],
              list_items: Array.isArray(data.list_items)
                ? data.list_items
                : undefined,
              for_city: data.for_city,
              for_country: data.for_country,
              created_at:
                data.created_at instanceof Timestamp
                  ? data.created_at.toDate()
                  : null,
              expires_at:
                data.expires_at instanceof Timestamp
                  ? data.expires_at.toDate()
                  : null,
              dismissed: !!data.dismissed,
              liked: !!data.liked,
            });
          });
          setCards(out);
          setIsLoading(false);
        },
        (err) => {
          console.warn('useGuideFeed listener error:', err);
          setIsLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      if (unsubSnap) unsubSnap();
    };
  }, []);

  // Imperative actions
  const dismiss = useCallback(async (cardId: string) => {
    const user = auth.currentUser;
    if (!user || !cardId) return;
    try {
      await updateDoc(doc(db, 'users', user.uid, 'guide_feed', cardId), {
        dismissed: true,
        dismissed_at: serverTimestamp(),
      });
    } catch (err) {
      console.warn('useGuideFeed.dismiss failed:', err);
    }
  }, []);

  const setLiked = useCallback(
    async (cardId: string, liked: boolean) => {
      const user = auth.currentUser;
      if (!user || !cardId) return;
      const cardRef = doc(db, 'users', user.uid, 'guide_feed', cardId);

      // Read the card BEFORE updating so we have the snapshot data
      // for the feed_item write — and the existing feed_item_id (if
      // any) so we can clean it up on unlike.
      let card: any = null;
      try {
        const snap = await getDoc(cardRef);
        if (snap.exists()) card = snap.data();
      } catch (err) {
        console.warn('setLiked: card read failed:', err);
      }

      // Update the like flag itself (existing behavior).
      try {
        await updateDoc(cardRef, {
          liked,
          liked_at: liked ? serverTimestamp() : null,
        });
      } catch (err) {
        console.warn('useGuideFeed.setLiked failed:', err);
        return;
      }

      // ── Feed bridge ──
      // A liked guide card is a "save" — bridge it to the social feed
      // as a kind=guide_save item. Unliking removes the bridged item.
      // Idempotent via feed_item_id stamp on the guide card itself.
      if (liked && card && !card.feed_item_id) {
        try {
          // Build snapshot with conditional fields — Firestore
          // rejects `undefined` values, so we only include keys
          // that have real data. (`null` is fine but `undefined`
          // throws "Unsupported field value".)
          const snapshot: Record<string, any> = {
            title: card.title || card.place_name || 'Saved card',
          };
          if (card.body || card.meta) {
            snapshot.caption = card.body || card.meta;
          }
          if (card.photo_url) {
            snapshot.image_path = card.photo_url;
            snapshot.preview_url = card.photo_url;
          }
          // Build a location object. Prefer specific point coords
          // when the card has them; always populate place_name from
          // the most specific text we have (place_name → city,
          // country → city → country). For guide cards that aren't
          // anchored to a point (tips/stories), this gives the feed
          // a "Berlin, Germany" chip instead of nothing.
          const placeName =
            card.place_name ||
            (card.for_city && card.for_country
              ? `${card.for_city}, ${card.for_country}`
              : card.for_city || card.for_country) ||
            undefined;
          const hasCoords =
            typeof card.location?.lat === 'number' &&
            typeof card.location?.lng === 'number';
          if (hasCoords || placeName) {
            const loc: Record<string, any> = {};
            if (hasCoords) {
              loc.lat = card.location.lat;
              loc.lng = card.location.lng;
            }
            if (placeName) loc.place_name = placeName;
            snapshot.location = loc;
          }
          const feedItemId = await writeFeedItem({
            kind: 'guide_save',
            snapshot,
            source: { kind: 'guide_card', id: cardId },
          });
          if (feedItemId) {
            await updateDoc(cardRef, { feed_item_id: feedItemId });
          }
        } catch (err) {
          console.warn('setLiked: feed bridge failed:', err);
        }
      } else if (!liked && card?.feed_item_id) {
        try {
          await deleteDoc(doc(db, 'feed_items', card.feed_item_id));
          await updateDoc(cardRef, { feed_item_id: null });
        } catch (err) {
          console.warn('setLiked: feed bridge cleanup failed:', err);
        }
      }
    },
    []
  );

  const remove = useCallback(async (cardId: string) => {
    const user = auth.currentUser;
    if (!user || !cardId) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'guide_feed', cardId));
    } catch (err) {
      console.warn('useGuideFeed.remove failed:', err);
    }
  }, []);

  return { cards, isLoading, dismiss, setLiked, remove };
};

// ────────────────────────────────────────────────────────────
// Background scheduler — mounted once at app root
// ────────────────────────────────────────────────────────────

const GENERATE_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/generateGuideCards';

const PERIODIC_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const SIGNIFICANT_MOVE_METERS = 2000; // 2 km — city-scale
const VISIBILITY_REFRESH_GRACE_MS = 2 * 60 * 1000; // 2 min away
const FEED_LOW_WATERMARK = 8;
const PRIME_BATCH_SIZE = 12;
const PERIODIC_BATCH_SIZE = 6;
const MOVE_BATCH_SIZE = 10;

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function fireGenerate(
  loc: { latitude: number; longitude: number },
  count: number,
  reason: 'prime' | 'topup' | 'refresh' | 'city_change',
  language?: string
): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  try {
    const token = await user.getIdToken();
    fetch(GENERATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        auth_token: token,
        location: { latitude: loc.latitude, longitude: loc.longitude },
        count,
        reason,
        // Hint forwarded to the cloud function. Server prefers
        // travelProfile.demographics.primaryLanguage; this fills
        // in for anonymous / pre-onboarding users.
        language,
      }),
    }).catch((err) => {
      console.warn('Guide feed generate request failed:', err);
    });
  } catch (err) {
    console.warn('Guide feed generate (token) failed:', err);
  }
}

/**
 * Mount once at the app root. Keeps the Guide feed warm regardless of
 * which tab the user is currently on.
 */
export const useGuideFeedBackground = () => {
  const { location } = useLocation();
  const profile = useAuthStore((s) => s.profile);
  const lastFiredAtRef = useRef<number>(0);
  const lastFiredLocationRef = useRef<{ lat: number; lng: number } | null>(
    null
  );
  const lastVisibleAtRef = useRef<number>(Date.now());
  const inflightRef = useRef<boolean>(false);
  const [feedSize, setFeedSize] = useState<number | null>(null);

  // Listen to feed size to know when to prime/top-up
  useEffect(() => {
    let unsubSnap: (() => void) | null = null;
    let currentUid: string | null = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user?.uid === currentUid) return;
      if (unsubSnap) {
        unsubSnap();
        unsubSnap = null;
      }
      currentUid = user?.uid || null;
      if (!user) {
        setFeedSize(null);
        return;
      }
      const ref = collection(db, 'users', user.uid, 'guide_feed');
      const q = query(ref, orderBy('created_at', 'desc'));
      unsubSnap = onSnapshot(
        q,
        (snap) => {
          // Count non-dismissed cards
          let n = 0;
          snap.forEach((d) => {
            if (!(d.data() as any)?.dismissed) n++;
          });
          setFeedSize(n);
        },
        (err) => {
          console.warn('useGuideFeedBackground size listener error:', err);
        }
      );
    });
    return () => {
      unsubAuth();
      if (unsubSnap) unsubSnap();
    };
  }, []);

  // Throttled fire wrapper
  const tryFire = useCallback(
    async (
      loc: { latitude: number; longitude: number },
      count: number,
      reason: 'prime' | 'topup' | 'refresh' | 'city_change',
      force = false
    ) => {
      if (inflightRef.current) return;
      const sinceLast = Date.now() - lastFiredAtRef.current;
      if (!force && sinceLast < 30_000) return;
      inflightRef.current = true;
      lastFiredAtRef.current = Date.now();
      lastFiredLocationRef.current = {
        lat: loc.latitude,
        lng: loc.longitude,
      };
      try {
        // Pass the language hint at fire time. Profile updates
        // close over the latest value via tryFire's deps, so a
        // user changing their language in Settings sees the next
        // top-up land in the new language.
        await fireGenerate(loc, count, reason, getLanguageHint(profile));
      } finally {
        // Quick in-flight reset; the actual server work continues
        // but we don't need to block re-firing on it
        setTimeout(() => {
          inflightRef.current = false;
        }, 3000);
      }
    },
    [profile]
  );

  // Trigger 1: initial prime (when feed is empty/low after mount)
  useEffect(() => {
    if (!location || feedSize === null) return;
    if (feedSize < FEED_LOW_WATERMARK) {
      const count = feedSize === 0 ? PRIME_BATCH_SIZE : PERIODIC_BATCH_SIZE;
      tryFire(location, count, feedSize === 0 ? 'prime' : 'topup');
    }
  }, [location, feedSize, tryFire]);

  // Trigger 2: periodic interval
  useEffect(() => {
    if (!location) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (!location) return;
      // Only top up if feed below watermark; otherwise let it ride
      if (feedSize !== null && feedSize >= FEED_LOW_WATERMARK + 6) return;
      tryFire(location, PERIODIC_BATCH_SIZE, 'topup');
    }, PERIODIC_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [location, feedSize, tryFire]);

  // Trigger 3: tab visibility change after grace period
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastVisibleAtRef.current = Date.now();
        return;
      }
      if (document.visibilityState === 'visible') {
        const awayMs = Date.now() - lastVisibleAtRef.current;
        if (awayMs > VISIBILITY_REFRESH_GRACE_MS && location) {
          tryFire(location, PERIODIC_BATCH_SIZE, 'topup');
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () =>
      document.removeEventListener('visibilitychange', onVisibility);
  }, [location, tryFire]);

  // Trigger 4: significant location change
  useEffect(() => {
    if (!location) return;
    const last = lastFiredLocationRef.current;
    if (!last) return; // first location handled by trigger 1
    const moved = haversine(
      last.lat,
      last.lng,
      location.latitude,
      location.longitude
    );
    if (moved >= SIGNIFICANT_MOVE_METERS) {
      // Force fire — bypass throttle
      tryFire(location, MOVE_BATCH_SIZE, 'city_change', true);
    }
  }, [location, tryFire]);
};

// ────────────────────────────────────────────────────────────
// Imperative top-up — used by GuidePage scroll trigger
// ────────────────────────────────────────────────────────────

export const useTriggerGuideFeed = () => {
  const { location } = useLocation();
  const profile = useAuthStore((s) => s.profile);
  const inflightRef = useRef<boolean>(false);
  const lastFiredAtRef = useRef<number>(0);

  return useCallback(
    async (
      reason: 'topup' | 'refresh' = 'topup',
      count = PERIODIC_BATCH_SIZE
    ) => {
      if (inflightRef.current) return;
      if (!location) return;
      const sinceLast = Date.now() - lastFiredAtRef.current;
      if (reason === 'topup' && sinceLast < 20_000) return;
      inflightRef.current = true;
      lastFiredAtRef.current = Date.now();
      try {
        // Same language hint as the background path. The profile
        // value is captured via the dep array so a Settings
        // change is reflected on the next user-initiated top-up.
        await fireGenerate(location, count, reason, getLanguageHint(profile));
      } finally {
        setTimeout(() => {
          inflightRef.current = false;
        }, 2000);
      }
    },
    [location, profile]
  );
};
